import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  HiOfficeBuilding,
  HiClipboardCheck,
  HiAcademicCap,
  HiChevronDown,
  HiChevronRight,
} from 'react-icons/hi';
import { useActivityFeed } from '@/api/hooks/useActivityLog';
import { PRIVILEGES } from '@/api/types/dtos';
import { useHasPrivilege } from '@/components/authorization';
import { useReportOrgSelection, type ReportOrg } from '@/hooks/useReportOrgSelection';
import ReportRequestDialog from '@/components/reports/ReportRequestDialog';
import LoadingSpinner from '@/components/LoadingSpinner';
import FarmDetailsModal from '@/components/modal/FarmDetailsModal';
import type { ActivityFeedItemDto } from '@/api/types/dtos/activity-log/activity-log.dto';
import { getActivityTypeLabel } from '../activityLabels';

const CATEGORY_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  ORGANIZATION: { icon: <HiOfficeBuilding size={20} />, color: '#2196f3', label: 'Organización' },
  INSPECTION: { icon: <HiClipboardCheck size={20} />, color: '#ff9800', label: 'Retos' },
  TRAINING: { icon: <HiAcademicCap size={20} />, color: '#4caf50', label: 'Capacitación' },
};

const ActivityFeed: React.FC = () => {
  const [category, setCategory] = useState<string>('');
  const [afterCursor, setAfterCursor] = useState<string | undefined>(undefined);
  const [allItems, setAllItems] = useState<ActivityFeedItemDto[]>([]);
  const [isLastPage, setIsLastPage] = useState(false);
  const prevCategoryRef = React.useRef(category);

  // Report-from-anywhere state (only for users with CREATE_REPORT)
  const { hasPrivilege: canCreateReport } = useHasPrivilege(PRIVILEGES.CREATE_REPORT);
  const selection = useReportOrgSelection();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const { data, isLoading } = useActivityFeed({
    category: category || undefined,
    after: afterCursor,
    size: 20,
  });

  React.useEffect(() => {
    if (data?.content) {
      if (afterCursor === undefined) {
        setAllItems(data.content);
      } else {
        setAllItems((prev) => [...prev, ...data.content]);
      }
      setIsLastPage(data.last);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  React.useEffect(() => {
    if (prevCategoryRef.current !== category) {
      prevCategoryRef.current = category;
      setAfterCursor(undefined);
      setAllItems([]);
      setIsLastPage(false);
      selection.clearSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  if (isLoading && allItems.length === 0) return <LoadingSpinner />;

  const handleLoadMore = () => {
    if (data?.nextCursor) {
      setAfterCursor(data.nextCursor);
    }
  };

  // Orgs referenced by the loaded feed items (deduped across items)
  const loadedOrgs: ReportOrg[] = [];
  const seenOrgIds = new Set<string>();
  allItems.forEach((item) => {
    (item.affectedOrganizationIds ?? []).forEach((orgId) => {
      if (orgId && !seenOrgIds.has(orgId)) {
        seenOrgIds.add(orgId);
        loadedOrgs.push({ id: orgId, name: item.affectedOrgNames?.[orgId] ?? orgId });
      }
    });
  });

  const handleSelectAllLoaded = () => {
    selection.selectMultiple(loadedOrgs);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Categoría</InputLabel>
          <Select
            value={category}
            label="Categoría"
            onChange={(e) => setCategory(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="ORGANIZATION">Organización</MenuItem>
            <MenuItem value="INSPECTION">Retos</MenuItem>
            <MenuItem value="TRAINING">Capacitación</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        {canCreateReport && loadedOrgs.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="text" size="small" onClick={handleSelectAllLoaded}>
              Seleccionar cargadas ({loadedOrgs.length})
            </Button>
            {selection.selectedCount > 0 && (
              <Button
                variant="text"
                size="small"
                color="inherit"
                onClick={selection.clearSelection}
              >
                Limpiar
              </Button>
            )}
            <Typography variant="body2" color="text.secondary">
              {selection.selectedCount} seleccionada{selection.selectedCount === 1 ? '' : 's'}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => setReportDialogOpen(true)}
              disabled={selection.selectedCount === 0}
            >
              Descargar Reporte
            </Button>
          </Box>
        )}
      </Box>

      {allItems.length === 0 && !isLoading && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No hay actividad registrada aún.
        </Typography>
      )}

      <List>
        {allItems.map((item) => (
          <FeedItem
            key={item.id}
            item={item}
            canSelect={canCreateReport}
            isSelected={selection.isSelected}
            onSelectOrgs={selection.selectMultiple}
            onDeselectOrgs={selection.unselectMultiple}
            onToggleOrg={(org) => selection.toggleSelection(org)}
          />
        ))}
      </List>

      {!isLastPage && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button onClick={handleLoadMore} disabled={isLoading}>
            {isLoading ? 'Cargando...' : 'Cargar Más'}
          </Button>
        </Box>
      )}

      {canCreateReport && (
        <ReportRequestDialog
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          orgs={selection.selectedOrgs}
          sourceLabel="Actividad"
        />
      )}
    </Box>
  );
};

const timeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `hace ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `hace ${days}d`;
};

interface FeedItemProps {
  item: ActivityFeedItemDto;
  canSelect: boolean;
  isSelected: (orgId: string) => boolean;
  onSelectOrgs: (orgs: ReportOrg[]) => void;
  onDeselectOrgs: (orgIds: string[]) => void;
  onToggleOrg: (org: ReportOrg) => void;
}

const FeedItem: React.FC<FeedItemProps> = ({
  item,
  canSelect,
  isSelected,
  onSelectOrgs,
  onDeselectOrgs,
  onToggleOrg,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [farmDetailsOpen, setFarmDetailsOpen] = useState(false);
  const config = CATEGORY_CONFIG[item.eventCategory] ?? CATEGORY_CONFIG.ORGANIZATION;

  const orgNames = item.affectedOrgNames ?? {};
  const affectedIds = item.affectedOrganizationIds ?? [];
  const hasMultipleOrgs =
    item.affectedOrgCount > 1 || (item.isGrouped && affectedIds.length > 1);
  const itemOrgs: ReportOrg[] = affectedIds.map((orgId) => ({
    id: orgId,
    name: orgNames[orgId] ?? orgId,
  }));
  const allItemOrgsSelected =
    itemOrgs.length > 0 && itemOrgs.every((org) => isSelected(org.id));
  const someItemOrgsSelected =
    !allItemOrgsSelected && itemOrgs.some((org) => isSelected(org.id));

  const handleToggleItemOrgs = () => {
    if (allItemOrgsSelected) {
      // Deselect only this item's orgs (keeps other selections intact)
      onDeselectOrgs(itemOrgs.map((org) => org.id));
    } else {
      onSelectOrgs(itemOrgs);
    }
  };

  const handleOrgClick = (orgId: string) => {
    setSelectedOrgId(orgId);
    setFarmDetailsOpen(true);
  };

  const handleFarmDetailsClose = () => {
    setFarmDetailsOpen(false);
    setSelectedOrgId(null);
  };

  return (
    <Paper sx={{ mb: 1 }}>
      {hasMultipleOrgs ? (
        <ListItem disablePadding>
          <ListItemButton onClick={() => setExpanded(!expanded)} sx={{ cursor: 'pointer' }}>
            {canSelect && (
              <ListItemIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleItemOrgs();
                }}
              >
                <Checkbox
                  edge="start"
                  checked={allItemOrgsSelected}
                  indeterminate={someItemOrgsSelected}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-label': `Seleccionar organizaciones de ${item.title}` }}
                />
              </ListItemIcon>
            )}
            <ListItemIcon sx={{ color: config.color, minWidth: 40 }}>
              {config.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {item.title}
                  </Typography>
                  <Chip
                    label={`${affectedIds.length} orgs`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {getActivityTypeLabel(item.eventType)} · {timeAgo(item.createdAt)}
                  {item.performedByName && ` · por ${item.performedByName}`}
                </Typography>
              }
            />
            {expanded ? <HiChevronDown size={16} /> : <HiChevronRight size={16} />}
          </ListItemButton>
        </ListItem>
      ) : (
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              const singleOrgId = affectedIds[0];
              if (singleOrgId) handleOrgClick(singleOrgId);
            }}
            sx={{ cursor: 'pointer' }}
          >
            {canSelect && (
              <ListItemIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleItemOrgs();
                }}
              >
                <Checkbox
                  edge="start"
                  checked={allItemOrgsSelected}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-label': `Seleccionar organizaciones de ${item.title}` }}
                />
              </ListItemIcon>
            )}
            <ListItemIcon sx={{ color: config.color, minWidth: 40 }}>
              {config.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={500}>
                  {item.title}
                </Typography>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {getActivityTypeLabel(item.eventType)} · {timeAgo(item.createdAt)}
                  {item.performedByName && ` · por ${item.performedByName}`}
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
      )}

      {hasMultipleOrgs && (
        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ pl: 6, pr: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, mb: 1 }}>
              {affectedIds.length} organización{affectedIds.length !== 1 ? 'es' : ''} afectada{affectedIds.length !== 1 ? 's' : ''}
            </Typography>
            <List disablePadding dense>
              {affectedIds.map((orgId) => (
                <ListItem key={orgId} disablePadding>
                  <ListItemButton onClick={() => handleOrgClick(orgId)} sx={{ borderRadius: 1, mb: 0.5 }}>
                    {canSelect && (
                      <ListItemIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleOrg({ id: orgId, name: orgNames[orgId] ?? orgId });
                        }}
                      >
                        <Checkbox
                          edge="start"
                          checked={isSelected(orgId)}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-label': `Seleccionar ${orgNames[orgId] || orgId}` }}
                        />
                      </ListItemIcon>
                    )}
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <HiOfficeBuilding size={16} color={config.color} />
                    </ListItemIcon>
                    <ListItemText
                      primary={orgNames[orgId] || orgId}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>
      )}

      {selectedOrgId && (
        <FarmDetailsModal
          open={farmDetailsOpen}
          onClose={handleFarmDetailsClose}
          organizationId={selectedOrgId}
        />
      )}
    </Paper>
  );
};

export default ActivityFeed;
